import * as THREE from 'three/webgpu';
import type { RawPointerEventPayload } from '../../viewer/moudles/EventManager';
import { isFeatureFace, isFeatureLine, isFeaturePoint } from '../../mesh/feature';
import { isDatumAxis } from '../../reference/DatumAxis';
import { isDatumPlaneObject } from '../../reference/DatumPlane';
import { isDatumPoint } from '../../reference/DatumPoint';
import type { EntitySelectionManager } from '../EntitySelectionManager';
import { DefaultEntitySelectionHandler } from './DefaultEntitySelectionHandler';

/**
 * 基准点模式实体选择处理器。
 *
 * 职责：
 * 1. 仅在基准点命令期间处理拾取与移入高亮；
 * 2. 点击命中的特征点/线/面或正式基准点/轴/面后，直接转换为基准点候选并交由控制器消费；
 * 3. 禁止默认模式下的拖拽、框选与普通语义选择流程。
 */
export class ReferencePointEntitySelectionHandler extends DefaultEntitySelectionHandler {
    /**
     * @param entitySelectionManager 实体选择管理器。
     */
    public constructor(entitySelectionManager: EntitySelectionManager) {
        super(entitySelectionManager);
    }

    /**
     * 处理鼠标按下。
     *
     * @param payload 原始事件。
     */
    public override onMouseDown(payload: RawPointerEventPayload): void {
        void payload;
    }

    /**
     * 处理鼠标移动。
     *
     * @param payload 原始事件。
     */
    public override onMouseMove(payload: RawPointerEventPayload): void {
        if (payload.buttons !== 0) {
            this.clearHoverState();
            return;
        }
        this.queueHoverInput(payload.clientX, payload.clientY);
    }

    /** @inheritdoc 使用当前模式的候选规则消费最新悬停坐标。 */
    public override consumeHoverInput(clientX: number, clientY: number): void {
        const hoveredPick = this.pickEntityAt(
            clientX,
            clientY,
            this.collectReferencePointSelectionTargets(),
        );
        this.updateHoverState(this.resolveHoverPickWithSelectionExclusion(hoveredPick));
    }

    /**
     * 处理鼠标抬起。
     *
     * @param payload 原始事件。
     */
    public override onMouseUp(payload: RawPointerEventPayload): void {
        void payload;
    }

    /**
     * 处理鼠标点击。
     *
     * @param payload 原始事件。
     */
    public override onClick(payload: RawPointerEventPayload): void {
        if (payload.button !== 0) {
            return;
        }
        if (this.entitySelectionManager.getSuppressNextClick()) {
            this.entitySelectionManager.setSuppressNextClick(false);
            return;
        }

        const rawPicked = this.pickEntityAt(
            payload.clientX,
            payload.clientY,
            this.collectReferencePointSelectionTargets(),
        );
        const picked = this.resolveClickSelectionPick(rawPicked);
        if (!picked) {
            return;
        }

        //------ 基准轴作为基准点引用 开始------
        if (
            !isFeatureFace(picked.object)
            && !isFeatureLine(picked.object)
            && !isFeaturePoint(picked.object)
            && !isDatumAxis(picked.object)
            && !isDatumPoint(picked.object)
            && !isDatumPlaneObject(picked.object)
        ) {
            return;
        }
        //------ 基准轴作为基准点引用 结束------

        const viewer = this.entitySelectionManager.renderer;
        const candidate = viewer.referencePointController.resolveSelectionCandidateByObjectId(picked.object.uuid);
        if (!candidate) {
            viewer.drawingNoticeCallback?.('当前实体不支持作为基准点参考', 'warning');
            return;
        }

        const accepted = viewer.referencePointController.handleSelection(candidate);
        if (!accepted) {
            viewer.drawingNoticeCallback?.('当前实体不支持作为基准点参考', 'warning');
            return;
        }

        // 成功写入后立即同步程序化高亮，保证视口与属性面板状态一致。
        viewer.referencePointController.syncSelectionVisuals();
    }

    /**
     * 收集框选候选对象。
     *
     * @returns 空候选列表。
     */
    protected override collectBoxSelectionTargets(): THREE.Object3D[] {
        return [];
    }

    /**
     * 收集测量叠加层点击候选对象。
     *
     * @returns 候选对象列表。
     */
    public collectMeasureClickSelectionTargets(): THREE.Object3D[] {
        return this.collectReferencePointSelectionTargets();
    }

    /**
     * 收集测量叠加层移入候选对象。
     *
     * @returns 候选对象列表。
     */
    public collectMeasureHoverSelectionTargets(): THREE.Object3D[] {
        return this.collectReferencePointSelectionTargets();
    }

    /**
     * 收集基准点模式的可拾取对象。
     *
     * @returns 可参与拾取的对象列表。
     */
    private collectReferencePointSelectionTargets(): THREE.Object3D[] {
        const featureAndDatumTargets = this.collectDatumReferenceInteractionTargets(this.collectClickSelectionTargets()).filter((candidate) => (
            isFeatureFace(candidate)
            || isFeatureLine(candidate)
            || isFeaturePoint(candidate)
            || isDatumAxis(candidate)
            || isDatumPoint(candidate)
            || isDatumPlaneObject(candidate)
        ));
        return this.mergeImportedTopologySelectionTargets(featureAndDatumTargets);
    }
}
